// pfs-search-input.ts — geteiltes, debounced Listen-Suchfeld.
//
// Ersetzt die je Cut-App-Bundle KOPIERTEN Helfer buildSearchInput/
// refocusSearchInput (identity, practice). Kanon „Listen-Filter backend-seitig"
// ([[feedback_list_filters_backend]]): onSearch/change re-fetcht serverseitig
// (?name=…), KEIN client-`.filter()`. Debounce verhindert Request-pro-Tastendruck.
//
// Attribute:
//   data-value         Startwert (Suchbegriff)
//   data-placeholder   Placeholder-Text
//   data-debounce      Debounce-Verzögerung in ms (Default 250)
//   data-name          optional: name + aria-label des Felds
//
// Property (per JS, live gelesen):
//   onSearch: (value:string) => void | Promise<…>  — bei (debounced) Änderung;
//             async erlaubt (fire-and-forget). Value ist getrimmt. Imperative
//             Bequemlichkeit für Builder wie pfs-list (PFS-664).
//
// Events (bubbles + composed):
//   change   — natives Form-Event, debounced (nach data-debounce ms); Wert via
//     el.value. Uniform mit den Form-Controls (PFS-663), NICHT pfs:search.
//
// Methoden / Properties am Element:
//   el.focusEnd()   — Fokus + Cursor ans Ende setzen (nach such-getriggertem
//                     Re-Render; ersetzt das alte refocusSearchInput).
//   el.value        — get/set des aktuellen Felds.
//
// Layout: das Rechtsbündig-Schieben (margin-left:auto) bleibt Consumer-Sache
//   (Toolbar-Kontext, je nach Scope-Toggle) — die WC stylt nur SICH selbst.
//   :host = inline-flex (NICHT inline-block) → kein Baseline-Strut-Versatz in
//   Toolbar-Flex-Zeilen neben Buttons/Toggle. Brandbar/dark via Tokens.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { CONTROL_CSS } from "../../styles/control.js";
import { invokeCallback } from "../../utils/invoke-callback.js";

/** Typisierte Consumer-Sicht auf <pfs-search-input>. */
export interface PfsSearchInputElement extends HTMLElement {
  value: string;
  onSearch?: (value: string) => void;
  focusEnd(pos?: number): void;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  display: inline-flex;
  min-width: var(--pfs-search-min-width, 14rem);
  box-sizing: border-box;
}
${CONTROL_CSS}
.search-input { flex: 1; min-width: 0; }
.search-input::placeholder { color: var(--pfs-color-on-surface-subtle); }
`;

defineComponent({
  tagName: "pfs-search-input",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;

    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const self = host as PfsSearchInputElement;
    const debounceMs = Math.max(0, parseInt(host.dataset.debounce ?? "250", 10) || 250);

    const input = document.createElement("input");
    input.type = "search";
    input.className = "search-input";
    input.setAttribute("part", "input");
    input.placeholder = host.dataset.placeholder ?? "";
    input.value = host.dataset.value ?? "";
    if (host.dataset.name) {
      input.name = host.dataset.name;
      input.setAttribute("aria-label", host.dataset.name);
    }
    root.appendChild(input);

    let timer: number | undefined;
    const fire = (): void => {
      const value = input.value.trim();
      // Debounced natives change (Wert via el.value) — uniform mit den Form-Controls.
      host.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
      // sync + async (fire-and-forget) tolerant; onSearch bekommt den getrimmten Wert.
      invokeCallback(self.onSearch, value);
    };
    const scheduleFire = (): void => {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(fire, debounceMs);
    };
    input.addEventListener("input", scheduleFire);
    ctx.cleanup.push(() => {
      if (timer) clearTimeout(timer);
      input.removeEventListener("input", scheduleFire);
    });

    // Imperative API am Element.
    // focusEnd(pos?) — Fokus + Cursor (Default ans Ende); für Refocus nach einem
    // such-getriggerten Re-Render.
    self.focusEnd = (pos?: number) => {
      input.focus();
      const p = typeof pos === "number" ? pos : input.value.length;
      input.setSelectionRange(p, p);
    };
    // value get/set. Der Setter triggert eine (debounced) Suche, WENN der Wert
    // sich ändert → ein programmatisch wiederhergestellter Live-Wert (nach Re-
    // Render, der den noch nicht abgesendeten Tipp-Stand übernimmt) zieht die
    // Suche nach, statt zu veralten. Gleicher Wert = kein Re-Fire (keine Schleife).
    Object.defineProperty(host, "value", {
      configurable: true,
      get: () => input.value,
      set: (v: string) => {
        const nv = v ?? "";
        if (nv === input.value) return;
        input.value = nv;
        scheduleFire();
      },
    });
  },
});
